// SunriseLayout — w08 Sahar (sunrise minimalist wedding).
//
// Design notes:
//   * Dawn pastel sky: peach at the bottom transitioning to soft blue at
//     the top — like the first 60 seconds of light before the sun crests.
//   * A soft sun glow disc sits low on the horizon, half-obscured behind
//     a tiny mountain silhouette (Chimgan ridge).
//   * Names sit above the horizon line in Cormorant Garamond italic,
//     small and intimate to feel like an elopement card rather than a
//     grand wedding announcement.
//   * Eyebrow at top: "AT FIRST LIGHT · CHIMGAN · 2026".
//   * Date + venue caption below the names in dawn-warm accent.
import type { LayoutCtx } from "./_shared";
import { Photo } from "./_shared";

let cardSeq = 0;
const nextCardId = () => `sahar-card-${++cardSeq}`;

export function SunriseLayout({
  names,
  date,
  venue,
  accent,
  ink,
  bg,
  font,
  photo,
  photos,
}: LayoutCtx) {
  const id = nextCardId();
  const heroSrc = photos[0] || photo;

  return (
    <div
      className={`${id}-card sahar-card`}
      style={{ position: "absolute", inset: 0, overflow: "hidden", background: bg }}
    >
      <style>{`
        @keyframes ${id}-glow {
          0%, 100% { opacity: 0.78; }
          50%      { opacity: 0.95; }
        }
        .${id}-glow { animation: ${id}-glow 5s ease-in-out infinite; }
        @media (prefers-reduced-motion: reduce) {
          .${id}-glow { animation: none !important; }
        }
      `}</style>

      {/* ── Dawn-gradient sky backdrop (peach bottom → soft blue top) ── */}
      <svg
        viewBox="0 0 200 280"
        preserveAspectRatio="none"
        style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none" }}
      >
        <defs>
          <linearGradient id={`${id}-sky`} x1="0" y1="0" x2="0" y2="1">
            {/* Soft dawn blue at the very top */}
            <stop offset="0%"  stopColor="#C9D6E2" stopOpacity="1" />
            {/* Cooler lavender blush */}
            <stop offset="22%" stopColor="#E5D5D0" stopOpacity="1" />
            {/* Peach band at the horizon */}
            <stop offset="55%" stopColor="#F2C8A0" stopOpacity="1" />
            {/* Warm sand at the foot */}
            <stop offset="85%" stopColor={bg} stopOpacity="1" />
            <stop offset="100%" stopColor={bg} stopOpacity="1" />
          </linearGradient>

          {/* Soft sun glow */}
          <radialGradient id={`${id}-sun`} cx="50%" cy="50%" r="50%">
            <stop offset="0%"  stopColor="#FFE6C4" stopOpacity="1" />
            <stop offset="55%" stopColor={accent}  stopOpacity="0.55" />
            <stop offset="100%" stopColor={accent} stopOpacity="0" />
          </radialGradient>
        </defs>

        <rect width="200" height="280" fill={`url(#${id}-sky)`} />

        {/* Wide soft sun glow disc behind the horizon */}
        <circle
          cx="100"
          cy="172"
          r="72"
          fill={`url(#${id}-sun)`}
          className={`${id}-glow`}
        />

        {/* Mountain silhouette — small Chimgan ridge across the horizon */}
        <g opacity="0.85">
          <path
            d="M 0 178 L 28 168 L 48 174 L 72 156 L 96 170 L 118 158 L 140 170 L 168 162 L 200 174 L 200 200 L 0 200 Z"
            fill={ink}
            fillOpacity="0.18"
          />
          <path
            d="M 0 188 L 22 180 L 50 184 L 78 172 L 108 184 L 134 176 L 162 184 L 200 180 L 200 210 L 0 210 Z"
            fill={ink}
            fillOpacity="0.28"
          />
        </g>

        {/* Tiny soaring bird marks (dawn flight) */}
        <g stroke={ink} strokeOpacity="0.35" strokeWidth="0.6" fill="none">
          <path d="M 134 86 q 3 -3 6 0 q 3 -3 6 0" />
          <path d="M 152 78 q 2 -2 4 0 q 2 -2 4 0" />
          <path d="M 60 92 q 2 -2 4 0 q 2 -2 4 0" />
        </g>
      </svg>

      {/* Optional photo wash — only if a photo is provided, sits as a
          translucent layer behind the sky for slight texture */}
      {heroSrc && (
        <Photo
          src={heroSrc}
          style={{
            position: "absolute",
            inset: 0,
            opacity: 0.18,
            mixBlendMode: "multiply",
          }}
        />
      )}

      {/* ── Eyebrow ─────────────────────────────────────────── */}
      <div
        style={{
          position: "absolute",
          top: "8%",
          left: 0,
          right: 0,
          textAlign: "center",
          fontFamily: "var(--font-ui)",
          fontSize: 7,
          fontWeight: 700,
          letterSpacing: "0.42em",
          color: accent,
        }}
      >
        AT FIRST LIGHT · CHIMGAN · 2026
      </div>

      {/* ── Names (above horizon) ───────────────────────────── */}
      <div
        style={{
          position: "absolute",
          top: "30%",
          left: 0,
          right: 0,
          textAlign: "center",
          padding: "0 8%",
        }}
      >
        <div
          style={{
            fontFamily: font,
            fontStyle: "italic",
            fontSize: 24,
            fontWeight: 400,
            color: ink,
            letterSpacing: "0.02em",
            lineHeight: 1.1,
          }}
        >
          {names}
        </div>
        {/* Hairline */}
        <div
          aria-hidden
          style={{
            width: 38,
            height: 1,
            background: accent,
            opacity: 0.6,
            margin: "10px auto 0",
          }}
        />
      </div>

      {/* ── Date + venue ────────────────────────────────────── */}
      <div
        style={{
          position: "absolute",
          bottom: "6%",
          left: 0,
          right: 0,
          textAlign: "center",
          fontFamily: "var(--font-ui)",
          fontSize: 7,
          fontWeight: 700,
          letterSpacing: "0.34em",
          color: accent,
        }}
      >
        {date} · {venue.toUpperCase()}
      </div>
    </div>
  );
}
